// Animation utilities for smooth, glassy effects
import { useEffect, useRef, useState } from "react";

// Intersection Observer hook for scroll animations
export const useInView = (options = {}) => {
  const [isInView, setIsInView] = useState(false);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setIsInView(true);
          setHasAnimated(true);
        }
      },
      {
        threshold: 0.1,
        rootMargin: "50px",
        ...options,
      }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [hasAnimated, options]);

  return [ref, isInView];
};

// Stagger animation hook for multiple elements
export const useStaggerAnimation = (delay = 100) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1, rootMargin: "50px" }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, []);

  return [ref, isVisible];
};

// Page load animation hook
export const usePageLoadAnimation = () => {
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  return isLoaded;
};

// Smooth scroll to element
export const smoothScrollTo = (elementId, offset = 0) => {
  const element = document.getElementById(elementId);
  if (element) {
    const elementPosition = element.getBoundingClientRect().top;
    const offsetPosition = elementPosition + window.pageYOffset - offset;

    window.scrollTo({
      top: offsetPosition,
      behavior: "smooth",
    });
  }
};

// Animation variants for Framer Motion
export const fadeInUp = {
  hidden: {
    opacity: 0,
    y: 40,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.25, 0, 1],
    },
  },
};

export const fadeInDown = {
  hidden: {
    opacity: 0,
    y: -40,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.25, 0, 1],
    },
  },
};

export const fadeInLeft = {
  hidden: {
    opacity: 0,
    x: -40,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.25, 0, 1],
    },
  },
};

export const fadeInRight = {
  hidden: {
    opacity: 0,
    x: 40,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    x: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.25, 0, 1],
    },
  },
};

export const scaleIn = {
  hidden: {
    opacity: 0,
    scale: 0.8,
  },
  visible: {
    opacity: 1,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.25, 0, 1],
    },
  },
};

export const glassyCard = {
  hidden: {
    opacity: 0,
    y: 30,
    scale: 0.95,
    backdropFilter: "blur(0px)",
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    backdropFilter: "blur(10px)",
    transition: {
      duration: 0.8,
      ease: [0.25, 0.25, 0, 1],
    },
  },
};

export const staggerContainer = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.1,
      delayChildren: 0.1,
    },
  },
};

export const staggerItem = {
  hidden: {
    opacity: 0,
    y: 20,
    scale: 0.95,
  },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.6,
      ease: [0.25, 0.25, 0, 1],
    },
  },
};

export const floatingAnimation = {
  animate: {
    y: [-10, 10, -10],
    transition: {
      duration: 4,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

export const pulseAnimation = {
  animate: {
    scale: [1, 1.05, 1],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut",
    },
  },
};

export const rotateAnimation = {
  animate: {
    rotate: [0, 360],
    transition: {
      duration: 20,
      repeat: Infinity,
      ease: "linear",
    },
  },
};

// CSS classes for additional animations
export const animationClasses = {
  // Glass morphism effects
  glassCard: "backdrop-blur-md bg-white/10 border border-white/20 shadow-xl",
  glassCardHover:
    "hover:bg-white/20 hover:border-white/30 hover:shadow-2xl transition-all duration-500",

  // Smooth transitions
  smoothTransition: "transition-all duration-500 ease-out",
  smoothTransitionFast: "transition-all duration-300 ease-out",
  smoothTransitionSlow: "transition-all duration-700 ease-out",

  // Hover effects
  hoverScale: "hover:scale-105 transition-transform duration-300 ease-out",
  hoverScaleSmall: "hover:scale-102 transition-transform duration-300 ease-out",
  hoverLift:
    "hover:-translate-y-2 hover:shadow-xl transition-all duration-300 ease-out",

  // Focus effects
  focusRing:
    "focus:ring-4 focus:ring-primary/20 focus:outline-none transition-all duration-300",

  // Loading animations
  shimmer:
    "animate-pulse bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 bg-[length:200%_100%]",
  fadeIn: "animate-fade-in",
  slideUp: "animate-slide-up",
  slideDown: "animate-slide-down",

  // Text animations
  textGradient:
    "bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent",
  textShine:
    "bg-gradient-to-r from-gray-900 via-primary to-gray-900 bg-[length:200%_auto] bg-clip-text text-transparent animate-shine",
};

// Custom Tailwind animations (add to tailwind.config.js)
export const customAnimations = {
  "fade-in": "fadeIn 0.8s ease-out forwards",
  "slide-up": "slideUp 0.8s ease-out forwards",
  "slide-down": "slideDown 0.8s ease-out forwards",
  "slide-left": "slideLeft 0.8s ease-out forwards",
  "slide-right": "slideRight 0.8s ease-out forwards",
  "scale-in": "scaleIn 0.6s ease-out forwards",
  float: "float 4s ease-in-out infinite",
  "pulse-soft": "pulseSoft 2s ease-in-out infinite",
  shine: "shine 2s ease-in-out infinite",
  "blur-in": "blurIn 0.8s ease-out forwards",
};

export const keyframes = {
  fadeIn: {
    "0%": { opacity: "0", transform: "translateY(20px)" },
    "100%": { opacity: "1", transform: "translateY(0)" },
  },
  slideUp: {
    "0%": { opacity: "0", transform: "translateY(40px)" },
    "100%": { opacity: "1", transform: "translateY(0)" },
  },
  slideDown: {
    "0%": { opacity: "0", transform: "translateY(-40px)" },
    "100%": { opacity: "1", transform: "translateY(0)" },
  },
  slideLeft: {
    "0%": { opacity: "0", transform: "translateX(40px)" },
    "100%": { opacity: "1", transform: "translateX(0)" },
  },
  slideRight: {
    "0%": { opacity: "0", transform: "translateX(-40px)" },
    "100%": { opacity: "1", transform: "translateX(0)" },
  },
  scaleIn: {
    "0%": { opacity: "0", transform: "scale(0.8)" },
    "100%": { opacity: "1", transform: "scale(1)" },
  },
  float: {
    "0%, 100%": { transform: "translateY(0px)" },
    "50%": { transform: "translateY(-10px)" },
  },
  pulseSoft: {
    "0%, 100%": { transform: "scale(1)" },
    "50%": { transform: "scale(1.05)" },
  },
  shine: {
    "0%": { backgroundPosition: "200% center" },
    "100%": { backgroundPosition: "-200% center" },
  },
  blurIn: {
    "0%": { opacity: "0", filter: "blur(10px)" },
    "100%": { opacity: "1", filter: "blur(0px)" },
  },
};

export default {
  useInView,
  useStaggerAnimation,
  usePageLoadAnimation,
  smoothScrollTo,
  fadeInUp,
  fadeInDown,
  fadeInLeft,
  fadeInRight,
  scaleIn,
  glassyCard,
  staggerContainer,
  staggerItem,
  floatingAnimation,
  pulseAnimation,
  rotateAnimation,
  animationClasses,
  customAnimations,
  keyframes,
};
